import React, {useCallback, useEffect, useRef, useState} from 'react';
import {css} from 'emotion';
import Text from '@amzn/meridian/text';
import Icon from '@amzn/meridian/icon';
import Popover from '@amzn/meridian/popover';
import Column from '@amzn/meridian/column';
import caretDownTokens from '@amzn/meridian-tokens/base/icon/caret-down';
import {useHistory, useLocation} from 'react-router-dom';
import {useAppSelector} from '../../../app/hooks';
import {selectCachedBusiness, selectCachedRegion} from '../../cached-state/cachedStateSlice';
import {getConstraintsList} from '../../../common/apis/BackendApisClient';
import {ConstraintRecord} from '../../../common/apis/models/getConstraintsListResponse';

const detailButtonStyle = css`
  background-color: transparent !important;
  border: none;
  cursor: pointer;
  padding: 0 !important;
  box-shadow: none !important;
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const mastheadTextStyle = css`
  margin-right: 2px !important;
  margin-left: 2px !important;
  color: black !important;
`;

const constraintItemStyle = css`
  cursor: pointer;
  padding: 8px 12px;
  &:hover {
    background-color: #f0f0f0;
  }
`;

const popoverContentStyle = css`
  max-height: 300px;
  overflow-y: auto;
  min-width: 220px;
`;

const ConstraintDropdown = () => {
  const {pathname} = useLocation();
  const history = useHistory();
  const businessType = useAppSelector(selectCachedBusiness);
  const region = useAppSelector(selectCachedRegion);

  const buttonRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [constraints, setConstraints] = useState<ConstraintRecord[]>([]);

  const onClickButton = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (businessType && region) {
      getConstraintsList({businessType, region, query: ''}).then((response) => {
        if (response) {
          setConstraints(response.records);
        }
      });
    }
  }, [businessType, region]);

  const handleConstraintClick = (constraint: ConstraintRecord) => {
    setOpen(false);
    history.push(`/constraints/${constraint.constraintName}-${constraint.constraintId}`);
  };

  if (pathname === '/select-business') {
    return null;
  }

  // Determine the label: show current constraint name if on a constraint page
  let dropdownLabel = 'Go to Constraint';
  if (pathname.startsWith('/constraints/')) {
    const constraintParam = pathname.replace('/constraints/', '');
    const parts = constraintParam.split('-');
    if (parts.length >= 1) {
      dropdownLabel = parts[0] || 'Constraint';
    }
  }

  return (
    <>
      <div className={detailButtonStyle} onClick={onClickButton} ref={buttonRef}>
        <Text type="h200" transform="uppercase" className={mastheadTextStyle}>
          {dropdownLabel}
        </Text>
        <Icon tokens={caretDownTokens} className={mastheadTextStyle} />
      </div>
      <Popover
        anchorNode={buttonRef.current || undefined}
        open={open}
        onClose={onClose}
        position="bottom"
        alignment="left"
      >
        <div className={popoverContentStyle}>
          <Column spacing="none">
            {constraints.map((constraint) => (
              <div
                key={constraint.constraintId}
                className={constraintItemStyle}
                onClick={() => handleConstraintClick(constraint)}
              >
                <Text type="b200">{constraint.constraintName}</Text>
                <Text type="b100" color="secondary"> ({constraint.inputGranularity})</Text>
              </div>
            ))}
            {constraints.length === 0 && (
              <div className={constraintItemStyle}>
                <Text type="b200" color="secondary">No constraints found</Text>
              </div>
            )}
          </Column>
        </div>
      </Popover>
    </>
  );
};

export default ConstraintDropdown;
